import type { LineChartProps } from '@shopify/polaris-viz';
import type { EMetricKey } from '~/types/metric';

import { usePageAnalyticDetailContext } from '~/modules/page-analytic';
import { EAnalyticDataType } from '~/core/gemxql/types';
import { useAnalyticData } from '~/core/gemxql/hooks';
import { useMemo } from 'react';

export const usePALineChartData = ({ activeTab }: { activeTab?: EMetricKey }) => {
  const { formatData } = useAnalyticData();

  const {
    hideComparison,
    allChartsDataMap,
    columnTypes,
    currentPeriodChartName,
    previousPeriodChartName,
  } = usePageAnalyticDetailContext();
  const formatter = activeTab ? columnTypes?.[activeTab] : undefined;

  const lineChartData = useMemo(() => {
    if (!activeTab || !allChartsDataMap?.[activeTab]) return [];

    const chartData = allChartsDataMap[activeTab];
    const currentChartData = {
      name: currentPeriodChartName,
      data: chartData.current,
    };
    if (hideComparison) return [currentChartData];

    return [
      currentChartData,
      {
        name: previousPeriodChartName,
        data: chartData.previous,
        isComparison: true,
      },
    ];
  }, [
    activeTab,
    currentPeriodChartName,
    previousPeriodChartName,
    hideComparison,
    allChartsDataMap,
  ]);

  const formatValue = (value: number) => {
    return String(formatData({ value, formatter }));
  };

  const yAxisOptions: LineChartProps['yAxisOptions'] = {
    labelFormatter: (value) => {
      return formatValue(Number(value) || 0);
    },
    // Empty % charts: override max to 0.1 so axis is 0%/5%/10% (matching count charts) instead of 0%/500%/1000%.
    ...(formatter === EAnalyticDataType.PERCENT ? { maxYOverride: 0.1 } : {}),
  };

  return { lineChartData, formatValue, yAxisOptions };
};
